// etx_scrollytelling.js
const SCROLLY_CONCURRENCY = 4;
const SCROLLY_PRELOAD_MARGIN = '200% 0px';

function etxScrollyTelling() {
  const modules = document.querySelectorAll(
    '[data-module="ScrollytellingVision"]'
  );

  if (!modules.length) return;
  if (typeof gsap === 'undefined') return;
  if (typeof ScrollTrigger === 'undefined') return;

  gsap.registerPlugin(ScrollTrigger);

  if (window.lenis && !window.__lenisScrollTriggerBound) {
    const updateScrollTrigger = () => ScrollTrigger.update();

    const lenisTicker = (time) => {
      window.lenis.raf(time * 1000);
    };

    window.lenis.on('scroll', updateScrollTrigger);
    gsap.ticker.add(lenisTicker);
    gsap.ticker.lagSmoothing(0);

    window.__lenisGsapTicker = true;
    window.__lenisScrollTriggerBound = true;
    window.__lenisGsapTickerCallback = lenisTicker;
    window.__lenisScrollTriggerCallback = updateScrollTrigger;
  }

  modules.forEach((module) => {
    if (module.dataset.scrollytellingInitialized === 'true') return;

    module.dataset.scrollytellingInitialized = 'true';

    const imageCount = parseInt(module.dataset.imageCount, 10) || 1;
    let cleanupSequence = null;

    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: module,
          start: 'top top',
          end: '+=125%',
          scrub: 0.35,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true
        }
      });

      /* ------------------------------
      IMAGE SEQUENCE
      ------------------------------ */

      if (imageCount > 1) {
        const canvas = module.querySelector('canvas');

        if (canvas) {
          cleanupSequence = etxScrollySequence(module, canvas, timeline, imageCount);
        }
      }

      /* ------------------------------
      SVG
      ------------------------------ */

      const svgPath = module.querySelector('.Vision__stripe svg path');

      if (svgPath) {
        const pathLength = svgPath.getTotalLength();

        gsap.set(svgPath, {
          strokeDasharray: pathLength,
          strokeDashoffset: pathLength
        });

        timeline.to(
          svgPath,
          { strokeDashoffset: 0, duration: 1, ease: 'none' },
          0
        );
      }

      /* ------------------------------
      TEXT
      ------------------------------ */

      const text1 = module.querySelector('.ScrollytellingVision__text_1');
      const text2 = module.querySelector('.ScrollytellingVision__text_2');

      if (text1) {
        timeline.fromTo(
          text1,
          { xPercent: -300 },
          { xPercent: 0, duration: 0.2, ease: 'power2.out' },
          0
        );
      }

      if (text2) {
        timeline.fromTo(
          text2,
          { xPercent: 300 },
          { xPercent: 0, duration: 0.2, ease: 'power2.out' },
          0
        );
      }
    }, module);

    module._scrollytellingContext = ctx;
    module._scrollytellingCleanup = () => {
      cleanupSequence?.();
      ctx.revert();
      delete module.dataset.scrollytellingInitialized;
    };
  });

  requestAnimationFrame(() => {
    window.lenis?.resize();
    ScrollTrigger.refresh();
  });
}

function etxScrollySequence(module, canvas, timeline, count) {
  const context = canvas.getContext('2d', { alpha: false });
  const { filePath, fileName, fileType } = module.dataset;
  const useImageKit = filePath.includes('ik.imagekit.io');

  // Index -> dekodiertes HTMLImageElement (erst gesetzt, wenn drawImage nicht mehr blockiert)
  const frames = new Array(count).fill(null);
  const state = { frame: 0 };

  let cssWidth = 0;
  let cssHeight = 0;
  let drawnIndex = -1;
  let requestWidth = 0;
  let loadingStarted = false;
  let destroyed = false;

  const setCanvasSize = (width, height) => {
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

    cssWidth = width;
    cssHeight = height;

    canvas.width = Math.round(cssWidth * pixelRatio);
    canvas.height = Math.round(cssHeight * pixelRatio);
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

    if (!requestWidth) {
      requestWidth = Math.ceil(canvas.width / 100) * 100;
    }

    drawnIndex = -1;
  };

  const frameUrl = (index) => {
    const url = `${filePath}/${fileName}_${index + 1}.${fileType}`;
    return useImageKit ? `${url}?tr=w-${requestWidth},q-70` : url;
  };

  const nearestLoaded = (target) => {
    for (let offset = 0; offset < count; offset++) {
      if (frames[target - offset]) return target - offset;
      if (frames[target + offset]) return target + offset;
    }
    return -1;
  };

  const render = () => {
    if (!cssWidth) return;

    const index = nearestLoaded(Math.round(state.frame));
    if (index === -1 || index === drawnIndex) return;

    drawnIndex = index;

    const image = frames[index];
    const ratio = Math.max(
      cssWidth / image.naturalWidth,
      cssHeight / image.naturalHeight
    );
    const width = image.naturalWidth * ratio;
    const height = image.naturalHeight * ratio;

    context.drawImage(
      image,
      (cssWidth - width) / 2,
      (cssHeight - height) / 2,
      width,
      height
    );

    canvas.classList.remove('is-loading');
  };

  // Erst und letzt, dann grob → fein: Scrubbing ist früh durchgehend nutzbar
  const loadOrder = () => {
    const order = [0, count - 1];
    const seen = new Set(order);

    [8, 4, 2, 1].forEach((step) => {
      for (let index = 0; index < count; index += step) {
        if (!seen.has(index)) {
          seen.add(index);
          order.push(index);
        }
      }
    });

    return order;
  };

  const loadFrame = (index) => {
    const image = new Image();
    image.decoding = 'async';
    image.src = frameUrl(index);

    return image
      .decode()
      .then(() => {
        if (destroyed) return;
        frames[index] = image;
        render();
      })
      .catch(() => {});
  };

  const startLoading = () => {
    if (loadingStarted) return;
    loadingStarted = true;

    const queue = loadOrder();
    const worker = async () => {
      while (queue.length && !destroyed) {
        await loadFrame(queue.shift());
      }
    };

    for (let i = 0; i < SCROLLY_CONCURRENCY; i++) worker();
  };

  setCanvasSize(canvas.clientWidth, canvas.clientHeight);

  const resizeObserver = new ResizeObserver(([entry]) => {
    const width = Math.round(entry.contentRect.width);
    const height = Math.round(entry.contentRect.height);

    if (!width || !height) return;
    if (width === cssWidth && height === cssHeight) return;

    setCanvasSize(width, height);
    render();
  });

  resizeObserver.observe(canvas);

  const intersectionObserver = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      intersectionObserver.disconnect();
      startLoading();
    },
    { rootMargin: SCROLLY_PRELOAD_MARGIN }
  );
  intersectionObserver.observe(module);

  timeline.to(
    state,
    {
      frame: count - 1,
      snap: 'frame',
      duration: 1,
      ease: 'none',
      onUpdate: render
    },
    0
  );

  return () => {
    destroyed = true;
    resizeObserver.disconnect();
    intersectionObserver.disconnect();
  };
}

// Theme-Editor: Section sauber abbauen, wenn sie entfernt/neu gerendert wird
document.addEventListener('shopify:section:unload', (event) => {
  event.target
    .querySelectorAll('[data-module="ScrollytellingVision"]')
    .forEach((module) => module._scrollytellingCleanup?.());
});